// src/components/EnquiryResults.js
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "./EnquiryResults.css";

/** Endpoints */
const ENQUIRY_BY_NRIC = "https://www.lianfoong.com/api/pawn-transactions/enquiry";
const ENQUIRY_BY_ITEM = "https://www.lianfoong.com/api/pawn-transactions/enquiry-by-item";

/** Format 'YYYY-MM-DD' or ISO strings safely to dd/MM/YYYY */
const fmtDDMMYYYY = (s) => {
  if (!s) return "-";
  const str = String(s);
  const datePart = str.split("T")[0]; // "2025-09-26"
  const parts = datePart.split("-");
  if (parts.length === 3) {
    const [yyyy, mm, dd] = parts;
    return `${dd.padStart(2, "0")}/${mm.padStart(2, "0")}/${yyyy}`;
  }
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return str;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/** Old ticket number helper (supports a few possible backend field names) */
const getOldTicketNo = (r) => {
  const v = r?.oldReceiptNo ?? r?.oldTicketNo ?? r?.oldTicketNo;
  if (v === null || v === undefined) return "-";
  const s = String(v).trim();
  return s ? s : "-";
};

export default function EnquiryResults() {
  const { mode } = useParams();
  const nav = useNavigate();
  const { state } = useLocation();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const cacheKey = `enquiry:${mode}:cache`;
    if (state?.value && state?.startDate && state?.endDate) {
      sessionStorage.setItem(cacheKey, JSON.stringify(state));
    } else {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached && !state) {
        nav(`/enquiry/${mode}/results`, { replace: true, state: JSON.parse(cached) });
        return;
      }
    }

    const run = async () => {
      try {
        if (!state?.startDate) return;

        if (mode === "nric") {
          const res = await axios.get(ENQUIRY_BY_NRIC, {
            params: { customerIc: state.value, startDate: state.startDate, endDate: state.endDate },
          });
          setRows(res.data || []);
        } else {
          const res = await axios.get(ENQUIRY_BY_ITEM, {
            params: { item: state.value, startDate: state.startDate, endDate: state.endDate },
          });
          setRows(res.data || []);
        }
      } catch (e) {
        setErr(e?.response?.data || "Search failed");
      }
    };

    run();
  }, [mode, state, nav]);

  const totals = useMemo(() => {
    if (!rows) return { grantCount: 0, redeemSum: 0 };
    const grantCount = rows.length;
    const redeemSum = rows.reduce((a, r) => a + Number(r.redeemAmount || 0), 0);
    return { grantCount, redeemSum };
  }, [rows]);

  const openTicket = (receiptNo) => {
    nav(`/ticket/${encodeURIComponent(receiptNo)}`, {
      state: { from: { pathname: `/enquiry/${mode}/results` } },
    });
  };

  return (
    <div className="enquiryResults">
      <div className="row space-between">
        <h2>
          Results — {mode === "nric" ? `NRIC: ${state?.value}` : `Item: ${state?.value}`}{" "}
          <small>({state?.months} months)</small>
        </h2>
        <button className="btn secondary" onClick={() => nav(-1)}>
          Change months
        </button>
      </div>

      {err && <div className="alert error">{String(err)}</div>}

      <div className="stats">
        <div className="stat">
          Total Grants: <b>{totals.grantCount}</b>
        </div>
        <div className="stat">
          Total Redeem Amount: <b>${totals.redeemSum.toFixed(2)}</b>
        </div>
      </div>

      {!rows ? (
        <div>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="empty">No results</div>
      ) : (
        <div className="table-wrapper">
          <table className="table">
            {/* <thead>
              <tr>
                <th>Ticket No.</th>
                <th>Old Ticket No.</th>
                <th>Grant Date</th>
                <th>Item</th>
                <th>Amount</th>
                <th>Redeem Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const oldNo = getOldTicketNo(r);

                return (
                  <tr key={r.receiptNo}>
                    <td>
                      <button className="linklike" onClick={() => openTicket(r.receiptNo)}>
                        {r.receiptNo}
                      </button>
                    </td>

                    <td>
                      {oldNo === "-" ? (
                        "-"
                      ) : (
                        <button className="linklike" onClick={() => openTicket(oldNo)}>
                          {oldNo}
                        </button>
                      )}
                    </td>

                    <td>{fmtDDMMYYYY(r.grantDate)}</td>
                    <td>{r.item}</td>
                    <td>{Number(r.amount || 0).toFixed(2)}</td>
                    <td>{Number(r.redeemAmount || 0).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody> */}
            <thead>
  <tr>
    <th>Old Ticket No.</th>
    <th>Ticket No.</th>
    <th>Grant Date</th>
    <th>Item</th>
    <th>Amount</th>
    <th>Redeem Amount</th>
  </tr>
</thead>

<tbody>
  {rows.map((r) => {
    const oldNo = getOldTicketNo(r);

    return (
      <tr key={r.receiptNo}>
        {/* OLD TICKET NO (first column now) */}
        <td>
          {oldNo === "-" ? (
            "-"
          ) : (
            <button className="linklike" onClick={() => openTicket(oldNo)}>
              {oldNo}
            </button>
          )}
        </td>

        {/* TICKET NO (second column now) */}
        <td>
          <button className="linklike" onClick={() => openTicket(r.receiptNo)}>
            {r.receiptNo}
          </button>
        </td>

        <td>{fmtDDMMYYYY(r.grantDate)}</td>
        <td>{r.item}</td>
        <td>{Number(r.amount || 0).toFixed(2)}</td>
        <td>{Number(r.redeemAmount || 0).toFixed(2)}</td>
      </tr>
    );
  })}
</tbody>

          </table>
        </div>
      )}

      <div className="row" style={{ marginTop: "1rem" }}>
        <button className="btn" onClick={() => nav("/enquiry")}>
          New Search
        </button>
      </div>
    </div>
  );
}
